import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NotificationService } from '../../../../../core/services/notification.service';
import { ICountry } from '../../../../../core/models/country.interface';
import { ICarrierItem,
    IProviderItem,
    IService,
    IShipmentPriceItemData,
    ISPItemData,
    ISubService,
    IVendor } from '../../../models/shipment-price.interface';
import { ShipmentPriceService } from '../../../services/shipment-price.service';
import { UserService } from 'src/app/core/services/user.service';

@Component({
    selector: 'eci-shipment-price-item-main-info',
    templateUrl: './item-main-info.component.html',
    styleUrls: [ './item-main-info.component.scss' ]
})
export class ItemMainInfoComponent implements OnInit {

    protected MAX_PERCENTAGE = 100;

    @Input() public displayFields: string[];
    @Input() public theShipmentPriceItem: IShipmentPriceItemData;
    @Input() public theVendor: IVendor;
    @Input() public theServiceType: IService;
    @Input() public theServiceSubType: ISubService;
    @Input() public theServiceProviders: ICarrierItem | IProviderItem; // Currently this is the carrier
    @Input() public theCountriesList: ICountry[];
    public hasSubCarrierList: { value: boolean; name: string }[] = [
        {value: true, name: 'Yes'},
        {value: false, name: 'No'}
    ];
    public typeUnitOfMeasurementList: { value: number; name: string }[] = [
        {value: 1, name: 'UK / EUR'},
        {value: 2, name: 'US'},
        // {value: 3, name: 'UK'},
    ];
    public showLoader: boolean = false;
    public submitted: boolean = false;
    public saveSPItemDataBtn: boolean;
    public form: FormGroup;

    constructor(
        private shipmentPriceService: ShipmentPriceService,
        public router: Router,
        public userSvc: UserService,
        private notify: NotificationService,
    ) {
    }

    ngOnInit(): void {
        this.form = this.initializeForm();
        this.subscribeOnPercentage('surcharges');
        this.subscribeOnPercentage('fixMargin');
        this.subscribeOnPercentage('defaultMargin');
    }

    private initializeForm(): FormGroup {
        const minCountryLength = 2;
        const minStringLength = 2;
        return new FormGroup({
            pricingId: new FormControl(
                {value: this.theShipmentPriceItem?.pricingId, disabled: true},
                [Validators.required]),
            serviceType: new FormControl(
                {value: this.theShipmentPriceItem?.shipmentService?.name, disabled: true},
                [Validators.required]),
            serviceSubType: new FormControl(
                {value: this.theShipmentPriceItem?.subService?.name, disabled: true},
                [Validators.required]),
            serviceProvider: new FormControl(
                {value: this.theShipmentPriceItem?.carrier?.name || this.theShipmentPriceItem?.serviceProvider?.name, disabled: true},
                [Validators.required]),
            vendor: new FormControl(
                {value: this.theVendor?.name, disabled: true},
                [Validators.required]),
            hasSubCarrier: new FormControl(
                {value: this.theShipmentPriceItem?.itemData?.hasSubCarrier || false, disabled: false},
                [Validators.required]),
            defaultMargin: new FormControl(
                {value: this.theShipmentPriceItem?.itemData?.defaultMargin || 0, disabled: false},
                [Validators.min(0), Validators.max(this.MAX_PERCENTAGE)]),
            origin: new FormControl(
                {value: this.theShipmentPriceItem?.itemData?.origin, disabled: false},
                [Validators.minLength(minCountryLength)]),
            coverage: new FormControl(
                {value: this.theShipmentPriceItem?.itemData?.coverage || 'EU', disabled: false},
                [Validators.minLength(minCountryLength)]),
            typeUnitOfMeasurement: new FormControl(
                {disabled: false, value: this.theShipmentPriceItem?.itemData?.typeUnitOfMeasurement || 1},
                [Validators.min(0)]),
            surcharges: new FormControl(
                {value: this.theShipmentPriceItem?.itemData?.surcharges || undefined, disabled: false},
                [Validators.min(0), Validators.max(this.MAX_PERCENTAGE)]),
            fixMargin: new FormControl(
                {value: this.theShipmentPriceItem?.itemData?.fixMargin || undefined, disabled: false},
                [Validators.min(0), Validators.max(this.MAX_PERCENTAGE)]),
            cost: new FormControl(
                {value: this.theShipmentPriceItem?.itemData?.cost || undefined, disabled: false},
                [Validators.min(0)]),
            price: new FormControl(
                {value: (this.theShipmentPriceItem?.itemData?.price || undefined), disabled: false},
                [Validators.min(0)]),
            currency: new FormControl(
                {value: this.theShipmentPriceItem?.itemData?.currency?.toUpperCase(), disabled: false},
                [Validators.minLength(minStringLength)]),
            searchCountry: new FormControl(''),
        });
    }

    public save(): void {
        this.submitted = true;
        this.saveSPItemDataBtn = true;
        if (this.form.invalid) {
            this.notify.showError('Not all fields are filled');
            this.form.markAllAsTouched();
            this.saveSPItemDataBtn = false;
            return;
        }

        const newData = {
            ...this.prepareBody,
        };

        if (this?.theShipmentPriceItem?.shipmentService?.name.toUpperCase() === 'FULFILLMENT') {

            this.shipmentPriceService.updateFulfillmentData('shipment-price', newData.shipmentPriceItemId, newData)
                .subscribe((data: IShipmentPriceItemData) => {
                    this.notify.showSuccessMessage('Shipment Price Data updated successfully');
                    this.updateForm(data);
                }, this.errorHandler);
        } else {
            this.shipmentPriceService.updateLastMileData('shipment-price', newData.shipmentPriceItemId, newData)
                .subscribe((data: IShipmentPriceItemData) => {
                    this.notify.showSuccessMessage('Shipment Price Data updated successfully');
                    this.updateForm(data);
                }, this.errorHandler);
        }
    }

    protected updateForm(data: IShipmentPriceItemData): void {
        this.form.patchValue({
            pricingId: data.pricingId,
            hasSubCarrier: data?.itemData?.hasSubCarrier || false,
            defaultMargin: data?.itemData?.defaultMargin,
            origin: data?.itemData?.origin,
            coverage: data?.itemData?.coverage,
            fixMargin: data?.itemData?.fixMargin,
            typeUnitOfMeasurement: data?.itemData?.typeUnitOfMeasurement,
            surcharges: data?.itemData?.surcharges,
            cost: data?.itemData?.cost,
            price: data?.itemData?.price,
            currency: data?.itemData?.currency,
            serviceProvider: (data?.carrier?.name || data?.serviceProvider?.name),
        });
        this.saveSPItemDataBtn = false;
    }

    private get prepareBody(): ISPItemData {
        return {
            shipmentPriceItemId: +this.theShipmentPriceItem.id,
            origin: this.form.controls?.origin?.value,
            coverage: this.form.controls?.coverage?.value,
            defaultMargin: this.form.controls?.defaultMargin?.value,
            fixMargin: this.form.controls?.fixMargin?.value,
            typeUnitOfMeasurement: this.form.controls?.typeUnitOfMeasurement?.value,
            surcharges: this.form.controls?.surcharges?.value,
            cost: this.form.controls?.cost?.value,
            price: this.form.controls?.price?.value,
            currency: this.form.controls?.currency?.value,
            hasSubCarrier: this.form.controls?.hasSubCarrier?.value,
        };
    }

    private errorHandler = (err) => {
        this.notify.showError(err.error.message);
        this.showLoader = false;
    }

    public get isAdmin(): boolean {
        return this.userSvc.isAdmin;
    }

    public changeCostPriceUnit(fieldName: string): void {
        if (this.form.get(fieldName)?.value !== '') {
            this.form.get(fieldName).setValue(parseFloat(this.form.get(fieldName)?.value || 0).toFixed(2));
        }
    }

    private subscribeOnPercentage(fieldName: string): void {
        this.form.get(fieldName).valueChanges
            .subscribe((data) => {
                if (data > this.MAX_PERCENTAGE) {
                    this.form.get(fieldName)
                        .setValue(this.MAX_PERCENTAGE);
                }
                if (data < 0) {
                    this.form.get(fieldName)
                        .setValue(0);
                }
            });
    }
}
